import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { CustomersPage } from '@/features/customers/CustomersPage';
import {
    type CustomersSearch,
    customersSearchFrom,
    customersUrlFrom,
} from '@/features/customers/filters';

/*
  The client register. The search, the three filters, the sort and the page ride the URL, so Back
  from a client returns to the list as it was left rather than to the whole register with the
  search gone (ALS-224, the rule ALS-105 set for every register), and a filtered view can be
  shared. `.catch(undefined)` keeps a junk value from throwing the route; what the values mean is
  `customersSearchFrom`'s business.
*/
const param = z.string().optional().catch(undefined);

const customersSearch = z.object({
    q: param,
    manager: param,
    country: param,
    status: param,
    sort: param,
    dir: param,
    page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/customers/')({
    validateSearch: (search) => customersSearch.parse(search),
    component: CustomersRoute,
});

function CustomersRoute() {
    const raw = Route.useSearch();
    const navigate = useNavigate();

    const search = useMemo(() => customersSearchFrom(raw), [raw]);

    const onSearchChange = useCallback(
        (next: CustomersSearch) =>
            // Replace, so typing a name is not one history entry per keystroke.
            void navigate({
                to: '/customers',
                search: customersUrlFrom(next),
                replace: true,
                resetScroll: false,
            }),
        [navigate],
    );

    return <CustomersPage search={search} onSearchChange={onSearchChange} />;
}
